import { ASPECT_RATIO_LABEL } from "./constants";
import { API_REGION, CONTENT_S3_BUCKET } from "src/scenes/App";

const DEFAULT_PREVIEW_SIZE = { width: "200px", height: "200px" };

const ASPECT_RATIO_PREVIEW_MAP = {
    [ASPECT_RATIO_LABEL.WIDESCREEN]: { width: "auto", height: "186.5px" },
    [ASPECT_RATIO_LABEL.PORTRAIT_4_5]: { width: "180px", height: "225px" },
    [ASPECT_RATIO_LABEL.WIDE_5_4]: { width: "300px", height: "200px" },
    [ASPECT_RATIO_LABEL.SQUARE]: { width: "250px", height: "250px" },
    [ASPECT_RATIO_LABEL.PORTRAIT]: { width: "150px", height: "267px" }
};

const parsePx = (val) => {
    if (typeof val === "number") return val;

    if (typeof val === "string") {
        if (val.endsWith("px")) return parseFloat(val);
        if (val === "auto") return 340;
        return parseFloat(val);
    }

    return null;
};

function getAspectRatioFromDimensions(width, height) {
    if (!width || !height) {
        return ASPECT_RATIO_LABEL.WIDESCREEN; // fallback
    }

    // Round ratio to 2 decimal places
    const ratio = width / height;
    const rounded = Math.round(ratio * 100);

    // Match known ratios
    if (rounded === 100) return ASPECT_RATIO_LABEL.SQUARE;
    if (rounded === 125) return ASPECT_RATIO_LABEL.WIDE_5_4;
    if (rounded === 80) return ASPECT_RATIO_LABEL.PORTRAIT_4_5;
    if (rounded === 178 || rounded === 177) return ASPECT_RATIO_LABEL.WIDESCREEN;
    if (rounded === 56) return ASPECT_RATIO_LABEL.PORTRAIT;

    // Default fallback
    return ASPECT_RATIO_LABEL.WIDESCREEN;
}

export class NoticeImage {
    constructor(urlSource, localDataBlob, width, height, uuid, standardAspectRatio) {

        console.log(`Image added with ${width} ${height}`);

        this.urlSource = urlSource;
        this.localDataBlob = localDataBlob;
        this.width = width;
        this.height = height;
        this.aspectRatio = ASPECT_RATIO_LABEL.isValid(standardAspectRatio) ?
            standardAspectRatio :
            getAspectRatioFromDimensions(width, height);
        this.uuid = uuid;

        this.localDataUrl = localDataBlob ?
            URL.createObjectURL(localDataBlob) :
            null;
    }

    free() {
        if (this.localDataUrl) {
            URL.revokeObjectURL(this.localDataUrl);
        }
    }

    pendingLocalUpload() {
        return this.localDataBlob;
    }

    getMediaAssetUrl() {
        return this.localDataUrl || this.urlSource || null;
    }

    mediaType() {
        return "image";
    }

    mediaPreviewSize() {
        const preview = ASPECT_RATIO_PREVIEW_MAP[this.aspectRatio];
        if (!preview) {
            return DEFAULT_PREVIEW_SIZE;
        }
        const width = preview.width === "auto" ? "340px" : preview.width;

        return {
            width,
            height: preview.height
        };
    }
}

export class NoticeVideo {
    constructor(urlSource, localDataBlob, standardAspectRatio, uuid, cropping, trimStart, trimEnd) {
        if (!ASPECT_RATIO_LABEL.isValid(standardAspectRatio)) {
            throw new Error(`Invalid image aspect ratio: ${standardAspectRatio}`);
        }
        this.urlSource = urlSource;
        this.localDataBlob = localDataBlob;
        this.uuid = uuid;
        this.aspectRatio = standardAspectRatio;
        this.cropping = cropping;
        this.trimStart = trimStart;
        this.trimEnd = trimEnd;

        this.localDataUrl = localDataBlob ?
            URL.createObjectURL(localDataBlob) :
            null;
    }

    free() {
        if (this.localDataUrl) {
            URL.revokeObjectURL(this.localDataUrl);
        }
    }

    pendingLocalUpload() {
        return this.localDataBlob;
    }

    getMediaAssetUrl() {
        return this.localDataUrl || this.urlSource || null;
    }

    mediaType() {
        return "video";
    }

    mediaPreviewSize() {
        const preview = ASPECT_RATIO_PREVIEW_MAP[this.aspectRatio];
        if (!preview) {
            return DEFAULT_PREVIEW_SIZE;
        }

        return {
            width: parsePx(preview.width) + "px",
            height: parsePx(preview.height) + "px"
        };
    }
}

const getPlainImageUrl = imageKey => {
    // Something like:
    // https://s3.ap-southeast-2.amazonaws.com/dailyfixme.content/eb647428-d0da-462b-9e27-9482c2713fc4/example.mp4
    return `https://s3.${API_REGION}.amazonaws.com/${CONTENT_S3_BUCKET}/${imageKey}`;
};

export function assetsFromExistingNotice(notice) {
    switch (notice.postType) {
        case "image":
            const image = new NoticeImage(
                getPlainImageUrl(notice.image.imageKey),
                null,
                notice.image.width,
                notice.image.height,
                notice.image.uuid
            );
            return [image];
        case "video":
            const video = new NoticeVideo(
                notice.video.videoUrl,
                null,
                notice.video.aspectRatio,
                notice.video.uuid
            );
            return [video];
        case "carousel":
            const items = [];
            notice.carousel.forEach((carouselItem) => {
                if (carouselItem.videoUrl) {
                    items.push(new NoticeVideo(
                        carouselItem.videoUrl,
                        null,
                        carouselItem.aspectRatio,
                        carouselItem.uuid
                    ));
                } else if (carouselItem.imageKey) {
                    items.push(new NoticeImage(
                        getPlainImageUrl(carouselItem.imageKey),
                        null,
                        carouselItem.width,
                        carouselItem.height,
                        carouselItem.uuid
                    ));
                }
            });
            return items;
        default:
            return [];
    }
}
